import { createSelector } from '@reduxjs/toolkit';
import { EachWorkdeskTicketI } from 'store/models/workdesk-tickets/each-workdesk-ticket.interface';
import { EachTicketGroup } from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import { Statuses, StatusI } from 'store/models/Statuses';
import { WorkdeskTickets } from 'store/models/workdesk-tickets/WorkdeskTickets';
import { inboxesDataMapSelector } from './inboxes.selector';
import getInboxesFromSelectedInboxes from 'store/helpers/getInboxesFromSelectedInboxes';

export const workdeskTicketsSelector = (state) =>
  state.workdeskTickets;

export const previewingBodyHtmlTicketSelector = createSelector(
  workdeskTicketsSelector,
  ({ previewingBodyHtmlId, ticketsMap }) => {
    if (!previewingBodyHtmlId) return null;
    if (!(previewingBodyHtmlId in ticketsMap))
      throw Error(
        `something is wrong. ticketId ${previewingBodyHtmlId} is not in the ticketsMap`,
      );
    if (!ticketsMap)
      throw Error(
        `something is wrong. ticketMap is null or undefined`,
      );
    return ticketsMap[previewingBodyHtmlId];
  },
);

export const isManualSiLoadingMapSelector = createSelector(
  workdeskTicketsSelector,
  (wdTickets) => {
    return wdTickets.isPostingManualSiIdMap;
  },
);

export const selectedStatusTicketGroupSelector = createSelector(
  (state) => state.workdeskFilter.cur,
  workdeskTicketsSelector,
  (
    wdFilter,
    wdTickets,
  ): null | {
    status: StatusI;
    isLoading: boolean;
    StatusIconClass: any;
    statusLabel: string;
    statusColor: string;
    ticketIds: string[];
  } => {
    const {
      selectedTicketType: ticketType,
      sortBy,
      selectedStatus,
      selectedInboxes,
      searchValues,
    } = wdFilter;
    const inboxes = getInboxesFromSelectedInboxes(selectedInboxes);
    const { hasLoaded, isLoadingStatuses, ticketsMap } = wdTickets;

    const status = selectedStatus;
    if (!status) return null;

    const matched = WorkdeskTickets.getFilteredSearchMatchedHasLoaded(
      { inboxes, status, ticketType, sortBy, searchValues },
      hasLoaded,
      ticketsMap,
    );
    const ticketIds = (matched && matched.ticketIds) || [];
    return {
      status,
      isLoading: isLoadingStatuses[status],
      StatusIconClass: Statuses.getIconOfStatus(status),
      statusLabel: Statuses.getLabelOfStatus(status, ticketType),
      statusColor: Statuses.getColorOfStatus(status),
      ticketIds,
    };
  },
);

export const isLoadingTicketsSelector = createSelector(
  workdeskTicketsSelector,
  ({ isLoading }) => isLoading,
);

/**
 * without sort
 */
export const selectedStatusTicketGroupWithTicketsSelector = createSelector(
  selectedStatusTicketGroupSelector,
  workdeskTicketsSelector,
  inboxesDataMapSelector,
  (
    ticketGroup,
    wdTickets,
    addressToInbox,
  ): EachTicketGroup | null => {
    if (!ticketGroup) return null;
    const {
      ticketsMap,
      isJustChangedTicketIdMap,
      assigneeIdToInfo,
    } = wdTickets;
    return {
      ...ticketGroup,
      tickets: WorkdeskTickets.formatTicketsDataForShow(
        ticketGroup.ticketIds.map((ticketId) => {
          const ticket: EachWorkdeskTicketI = ticketsMap[ticketId];
          return ticket;
        }),
        assigneeIdToInfo,
        isJustChangedTicketIdMap,
        addressToInbox,
      ),
    };
  },
);

export const newJustChangedTicketIdsSelector = createSelector(
  workdeskTicketsSelector,
  ({ isJustChangedTicketIdMap }): string[] => {
    if (!isJustChangedTicketIdMap) return [];
    return Object.keys(isJustChangedTicketIdMap).filter(
      (id) => isJustChangedTicketIdMap[id] === 'NEW',
    );
  },
);

export const fromWsTicketIdsSelector = createSelector(
  workdeskTicketsSelector,
  ({ fromWsTicketIds }): string[] => {
    return fromWsTicketIds || [];
  },
);

/**
 * only the new
 */
export const justChangedAndFromWsTicketIdsSelector = createSelector(
  newJustChangedTicketIdsSelector,
  fromWsTicketIdsSelector,
  (newJustChangedTicketIds, fromWsTicketIds): string[] => {
    return newJustChangedTicketIds.concat(fromWsTicketIds);
  },
);

export const ticketsMapSelector = createSelector(
  workdeskTicketsSelector,
  (wdTickets) => {
    return wdTickets.ticketsMap;
  },
);

export const latestWithSearchTicketsCountSelector = createSelector(
  workdeskTicketsSelector,
  (wdTickets) => {
    return wdTickets.latestWithSearchTicketsCount;
  },
);

/**
 * used in wdTicketsNofromWsLengthSelector below
 */
export const wdTicketsNofromWsSelector = createSelector(
  selectedStatusTicketGroupWithTicketsSelector,
  (ticketGroup) =>
    ticketGroup?.tickets.filter((ticket) => !ticket.fromWs),
);

/**
 * used to check api length when search if it matches the shown length
 */
export const wdTicketsNofromWsLengthSelector = createSelector(
  wdTicketsNofromWsSelector,
  (tickets) => tickets?.length,
);

export const allInboxesStatusLatestUpdateDateSelector = createSelector(
  workdeskTicketsSelector,
  (storeState) => storeState.allInboxesStatusLatestUpdateDate,
);
